import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { 
  FaArrowLeft, FaCheckCircle, FaMapMarkerAlt, FaStar, 
  FaRegCalendarAlt, FaPrint, FaDownload, FaBed, FaHashtag,
  FaEnvelope, FaBolt, FaTimesCircle, FaHourglassHalf, FaInfoCircle
} from "react-icons/fa";
import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import Swal from "sweetalert2"; 
import { baseurl } from "../../baseurl";
import "./HotelBookingInvoice.css";

const HotelBookingInvoice = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invoiceRef = useRef();
  const bookingId = searchParams.get("booking");
  const userId = localStorage.getItem("guestUserId");
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    if (!bookingId) {
      setError("No booking ID found.");
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${baseurl}/getBookingDetails/${bookingId}`);
      if (res.data.status === "success") {
        setBooking(res.data.data);
      } else {
        setError("Booking not found.");
      }
    } catch (err) {
      setError("Failed to load booking details.");
    } finally {
      setLoading(false);
    }
  };

  // --- DYNAMIC REFUND CALCULATION ---
  const calculateRefund = () => {
    if (!booking) return { percent: 0, amount: 0, days: 0 };
    
    const today = new Date();
    const checkIn = new Date(booking.check_in);
    const diffTime = checkIn - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let deductionPercent = 20; 
    if (diffDays <= 0) deductionPercent = 100;
    else if (diffDays === 1) deductionPercent = 50;
    else if (diffDays === 2) deductionPercent = 45;
    else if (diffDays === 3) deductionPercent = 40;
    else if (diffDays === 4) deductionPercent = 35;
    else if (diffDays === 5) deductionPercent = 30;

    const total = parseFloat(booking.total_price);
    const deductionAmount = total * (deductionPercent / 100);
    const refundAmount = total - deductionAmount;

    return { 
      percent: deductionPercent, 
      refund: refundAmount, 
      deduction: deductionAmount,
      days: diffDays 
    };
  };

  const handleCancelRequest = async () => {
    const policy = calculateRefund();
    
    const { value: reason, isConfirmed } = await Swal.fire({
      title: 'Request Cancellation?',
      html: `
        <div style="text-align: left; font-size: 14px; background: #fff1f1; padding: 10px; border-radius: 8px; border: 1px solid #ffcccc; margin-bottom: 15px;">
            <p style="margin: 0; color: #d33;"><b>Refund Summary:</b></p>
            <p style="margin: 5px 0 0 0;">Days to Check-in: <b>${policy.days} Day(s)</b></p>
            <p style="margin: 2px 0 0 0;">Deduction: <b>${policy.percent}% (-₹${policy.deduction.toLocaleString()})</b></p>
            <p style="margin: 2px 0 0 0; font-size: 16px; color: #198754;">Estimated Refund: <b>₹${policy.refund.toLocaleString()}</b></p>
        </div>
      `,
      input: 'textarea',
      inputLabel: 'Reason for cancellation',
      inputPlaceholder: 'Enter your reason here...',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Submit Request',
      inputValidator: (value) => {
        if (!value) return 'Please provide a reason!'
      }
    });

    if (!isConfirmed) return;

    try {
      setCancelling(true);
      const res = await axios.post(`${baseurl}/requestCancellation`, {
        bookingId: bookingId,
        userId: userId,
        reason: reason
      });

      if (res.data.status === "success") {
        await Swal.fire('Request Sent!', 'Admin will review and refund the balance to your wallet.', 'success');
        fetchBookingDetails();
      } else {
        Swal.fire('Error', res.data.message || "Request failed.", 'error');
      }
    } catch (err) {
      Swal.fire('Error', "Error processing request.", 'error');
    } finally {
      setCancelling(false);
    }
  };

  const handlePrint = () => window.print();

  const handleDownload = async () => {
    const element = invoiceRef.current;
    const canvas = await html2canvas(element, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`Invoice-${booking.booking_reference}.pdf`);
  };

  if (loading) return <div className="inv-loader">Generating your invoice...</div>;
  if (error) return <div className="inv-error">{error}</div>;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const status = booking.booking_status.toLowerCase();
  const isCancelled = status === "cancelled";
  const isPendingCancel = status === "cancellation_pending";
  const refundPolicy = calculateRefund();

  return (
    <div className="inv-page">
      <nav className="inv-nav no-print">
        <div className="inv-container">
          <button onClick={() => navigate("/")} className="inv-back-btn">
            <FaArrowLeft /> Back to Home
          </button>
          <div className="inv-nav-actions">
            {!isCancelled && !isPendingCancel && (
               <button onClick={handleCancelRequest} className="btn-action cancel" disabled={cancelling}>
                 <FaTimesCircle /> {cancelling ? "Requesting..." : "Cancel Ticket"}
               </button>
            )}
            {isPendingCancel && (
               <button className="btn-action pending" disabled>
                 <FaHourglassHalf /> Cancellation Pending
               </button>
            )}
            <button onClick={handleDownload} className="btn-action download"><FaDownload /> Download</button>
            <button onClick={handlePrint} className="btn-action print"><FaPrint /> Print</button>
          </div>
        </div>
      </nav>

      <div className="inv-container" ref={invoiceRef}>
        <div className="inv-card">
          
          <div className="brand-header">
            <div className="brand-left">
              <div className="brand-logo-circle"><FaBolt /></div>
              <div className="brand-title">
                <h2>TopAirView</h2>
                <p>Premium Travel & Booking Services</p>
              </div>
            </div>
            <div className="brand-right">
              <h3>{isCancelled ? "CANCELLATION RECEIPT" : "PAYMENT RECEIPT"}</h3>
              <p>Ref: {booking.booking_uuid.toUpperCase()}</p>
            </div>
          </div>

          <div className="inv-transaction-bar">
            <span>Date: {formatDate(new Date())}</span>
            <span className={`status-pill ${isCancelled ? 'danger' : isPendingCancel ? 'warning' : 'success'}`}>
              {status.replace('_', ' ').toUpperCase()}
            </span>
          </div>

          <div className="inv-main-content">
            <div className="inv-details">
              <div className="inv-section-header">
                <h1>{isCancelled ? "Cancellation Invoice" : isPendingCancel ? "Cancellation Requested" : "Booking Invoice"}</h1>
                <p className="ref-no">Reference: <strong>{booking.booking_reference}</strong></p>
              </div>

              <div className="inv-grid">
                <div className="inv-group">
                  <label>Hotel Details</label>
                  <h3>{booking.hotel_name}</h3>
                  <p className="inv-text-sm"><FaMapMarkerAlt /> {booking.hotel_address}, {booking.city_village}</p>
                </div>
                <div className="inv-group text-end">
                  <label>Room Type</label>
                  <p className="inv-val"><FaBed /> {booking.room_type_name}</p>
                </div>
              </div>

              <hr className="inv-hr" />

              <div className="inv-grid">
                <div className="inv-group">
                  <label>Check-In</label>
                  <p className="inv-val"><FaRegCalendarAlt /> {formatDate(booking.check_in)}</p>
                </div>
                <div className="inv-group text-end">
                  <label>Check-Out</label>
                  <p className="inv-val"><FaRegCalendarAlt /> {formatDate(booking.check_out)}</p>
                </div>
              </div>

              {/* DEDUCTION CHART SECTION */}
              {!isCancelled && !isPendingCancel && (
                <div className="refund-policy-section mt-4">
                    <h5 className="policy-title"><FaInfoCircle /> Cancellation Refund Policy</h5>
                    <div className="policy-table">
                        <div className="policy-row"><span>6+ Days</span><span>20% Deduct</span></div>
                        <div className="policy-row"><span>5 Days</span><span>30% Deduct</span></div>
                        <div className="policy-row"><span>4 Days</span><span>35% Deduct</span></div>
                        <div className="policy-row"><span>3 Days</span><span>40% Deduct</span></div>
                        <div className="policy-row"><span>2 Days</span><span>45% Deduct</span></div>
                        <div className="policy-row"><span>1 Day</span><span>50% Deduct</span></div>
                    </div>
                </div>
              )}

              <hr className="inv-hr" />

              <div className="inv-group guest">
                <label>Guest Information</label>
                <p><strong>{booking.user_name}</strong></p>
                <p>{booking.user_email} • {booking.user_phone}</p>
              </div>
            </div>

            <div className="inv-summary-sidebar">
              <div className={`summary-inner ${isCancelled ? 'cancelled-sidebar' : isPendingCancel ? 'pending-sidebar' : ''}`}>
                <h4>{isCancelled ? "Refund Summary" : "Payment Summary"}</h4>
                <div className="summary-row"><span>Total Paid</span><span>₹{booking.total_price}</span></div>
                
                {isCancelled || isPendingCancel ? (
                    <>
                        <div className="summary-row text-danger"><span>Deduction Applied</span><span>-₹{refundPolicy.deduction.toLocaleString()}</span></div>
                        <div className="summary-total text-success">
                            <span>Refund Amount</span>
                            <span>₹{refundPolicy.refund.toLocaleString()}</span>
                        </div>
                    </>
                ) : (
                    <div className="summary-total">
                        <span>Grand Total</span>
                        <span>₹{booking.total_price}</span>
                    </div>
                )}

                <div className="payment-id">
                    <small>
                        {isCancelled ? "Refunded to Wallet" : 
                         isPendingCancel ? "Awaiting Admin Approval" : `TXN: ${booking.razorpay_payment_id}`}
                    </small>
                </div>
              </div>
            </div>
          </div>

          <div className="brand-footer">
            <div className="footer-col">
              <h5 className="footer-label">Support</h5>
              <p><FaEnvelope /> a2zyatra2012@gmail.com</p>
            </div>
            <div className="footer-col">
              <h5 className="footer-label">Location</h5>
              <p><FaMapMarkerAlt /> Haridwar, UK (249401)</p>
            </div>
            <div className="footer-col brand-col">
              <h4>TopAirView Solutions</h4>
              <p>Empowering Digital Payments</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelBookingInvoice;